import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ArrowRight, Eye, EyeOff } from "lucide-react";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/context/AuthContext";
import { errMsg } from "@/lib/api";

const HERO = "/login-bg.png";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim(), password);
      navigate("/tableau-de-bord");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-[1.2fr_1fr]">
      <div className="relative hidden overflow-hidden bg-[#0F172A] lg:block">
        <img src={HERO} alt="Arrière-plan" className="absolute inset-0 h-full w-full object-cover opacity-80" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/40 to-transparent" />
        <div className="relative flex h-full flex-col justify-end p-14 text-white">
          <p className="mb-3 text-xs font-semibold uppercase tracking-[0.3em] text-red-400">Plateforme de gestion interne</p>
          <h1 className="font-display text-5xl font-extrabold leading-tight">
            Assurer la durabilité<br />et la performance<br />de vos ouvrages.
          </h1>
          <p className="mt-6 max-w-md text-sm text-slate-300">
            Gestion commerciale, interventions, personnel et services — un seul espace pour piloter A.C.E.S S.A.R.L.
          </p>
          <div className="mt-10 flex gap-6 text-[11px] uppercase tracking-[0.2em] text-slate-400">
            <span>Intégrité</span><span className="text-red-500">/</span><span>Innovation</span><span className="text-red-500">/</span><span>Fiabilité</span>
          </div>
        </div>
      </div>
      <div className="grid-bg flex items-center justify-center bg-white px-6 py-12">
        <form onSubmit={submit} className="w-full max-w-sm fade-up" data-testid="login-form">
          <Link to="/" className="mb-6 inline-block text-xs font-semibold text-slate-500 hover:text-red-600" data-testid="login-back-site">← Retour au site</Link>
          <img src="/logo.png" alt="ACES" className="mb-10 h-24 w-auto" />
          <h2 className="font-display text-3xl font-bold text-slate-900">Connexion</h2>
          <p className="mt-2 text-sm text-slate-500">Accès réservé au personnel d'A.C.E.S. Les comptes sont créés par la direction.</p>
          <div className="mt-8 space-y-4">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</label>
              <Input data-testid="login-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nom@exemple.com" />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Mot de passe</label>
              <div className="relative">
                <Input data-testid="login-password-input" type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-2 top-2 text-slate-400 hover:text-slate-700" data-testid="login-toggle-password" title={show ? "Masquer" : "Afficher"}>
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && <p data-testid="login-error" className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="group flex h-11 w-full items-center justify-center gap-2 rounded-md bg-red-600 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:opacity-60"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Se connecter <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" /></>}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
